import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { MovementMilestone } from "@tih/shared";
import {
  adminCreateMovementMilestone,
  adminDeleteMovementMilestone,
  adminFetchBlog,
  adminFetchGalleryAlbums,
  adminUpdateMovementMilestone,
  ApiError,
} from "../api/client";
import { milestoneDateForSave, milestoneDateFromInput, milestoneDateToInput } from "../lib/milestoneDate";
import { SearchableSelect } from "./SearchableSelect";

type LinkKind = "none" | "blog" | "gallery";

export type MilestoneDraft = {
  id: number | null;
  year: string;
  title: string;
  body: string;
  sortOrder: number;
  linkKind: LinkKind;
  blogPostId: number | null;
  galleryAlbumId: number | null;
};

type Props = {
  open: boolean;
  draft: MilestoneDraft;
  onClose: () => void;
  onSaved: (milestone: MovementMilestone) => void;
  onDeleted?: (id: number) => void;
};

export function emptyMilestoneDraft(sortOrder: number): MilestoneDraft {
  return {
    id: null,
    year: "",
    title: "",
    body: "",
    sortOrder,
    linkKind: "none",
    blogPostId: null,
    galleryAlbumId: null,
  };
}

export function milestoneToDraft(m: MovementMilestone): MilestoneDraft {
  return {
    id: m.id,
    year: m.year,
    title: m.title,
    body: m.body,
    sortOrder: m.sortOrder,
    linkKind: m.blogPostId != null ? "blog" : m.galleryAlbumId != null ? "gallery" : "none",
    blogPostId: m.blogPostId,
    galleryAlbumId: m.galleryAlbumId,
  };
}

export function TimelineMilestoneEditModal({
  open,
  draft: initialDraft,
  onClose,
  onSaved,
  onDeleted,
}: Props) {
  const [draft, setDraft] = useState(initialDraft);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [blogPosts, setBlogPosts] = useState<
    { id: number; title: string; slug: string; publishedAt: string | null }[]
  >([]);
  const [albums, setAlbums] = useState<{ id: number; title: string; slug: string }[]>([]);

  useEffect(() => {
    if (!open) return;
    setDraft(initialDraft);
    setErr(null);
  }, [initialDraft, open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !saving && !deleting) onClose();
    }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, saving, deleting]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    Promise.all([adminFetchBlog(), adminFetchGalleryAlbums()])
      .then(([posts, galleryAlbums]) => {
        if (cancelled) return;
        setBlogPosts(
          posts.map((p) => ({
            id: p.id,
            title: p.title,
            slug: p.slug,
            publishedAt: p.publishedAt,
          })),
        );
        setAlbums(galleryAlbums.map((a) => ({ id: a.id, title: a.title, slug: a.slug })));
      })
      .catch(() => {
        if (!cancelled) setErr("Could not load blog posts or albums");
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  function setLinkKind(next: LinkKind) {
    setDraft((d) => ({
      ...d,
      linkKind: next,
      blogPostId: next === "blog" ? d.blogPostId : null,
      galleryAlbumId: next === "gallery" ? d.galleryAlbumId : null,
    }));
  }

  async function onSave() {
    setSaving(true);
    setErr(null);
    const dateValue = milestoneDateFromInput(draft.year);
    if (!dateValue) {
      setErr("Choose a date");
      setSaving(false);
      return;
    }
    const payload = {
      year: milestoneDateForSave(dateValue, initialDraft.year),
      title: draft.title.trim(),
      body: draft.body.trim(),
      sortOrder: draft.sortOrder,
      blogPostId: draft.linkKind === "blog" ? draft.blogPostId : null,
      galleryAlbumId: draft.linkKind === "gallery" ? draft.galleryAlbumId : null,
    };
    try {
      const saved =
        draft.id == null
          ? await adminCreateMovementMilestone(payload)
          : await adminUpdateMovementMilestone(draft.id, payload);
      onSaved(saved);
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    if (draft.id == null || !onDeleted) return;
    if (!window.confirm("Delete this milestone?")) return;
    setDeleting(true);
    setErr(null);
    try {
      await adminDeleteMovementMilestone(draft.id);
      onDeleted(draft.id);
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Delete failed");
    } finally {
      setDeleting(false);
    }
  }

  const blogPostOptions = blogPosts.map((post) => ({
    value: post.id,
    label: post.title,
    hint: post.publishedAt
      ? new Date(post.publishedAt).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : post.slug,
  }));

  const busy = saving || deleting;

  return createPortal(
    <div
      className="copy-text-modal-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        className="copy-text-modal timeline-milestone-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="timeline-milestone-modal-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="timeline-milestone-modal-header">
          <h3 id="timeline-milestone-modal-title">
            {draft.id == null ? "Add milestone" : "Edit milestone"}
          </h3>
          <button
            type="button"
            className="timeline-milestone-modal-close"
            aria-label="Close milestone form"
            disabled={busy}
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="timeline-milestone-fields">
          <label>
            <span>Date</span>
            <input
              type="date"
              value={milestoneDateToInput(draft.year)}
              onChange={(e) =>
                setDraft((d) => ({ ...d, year: milestoneDateFromInput(e.target.value) }))
              }
            />
          </label>
          <label>
            <span>Title</span>
            <input
              type="text"
              value={draft.title}
              maxLength={200}
              onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
            />
          </label>
          <label className="timeline-milestone-field-wide">
            <span>Story</span>
            <textarea
              rows={5}
              value={draft.body}
              onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
            />
          </label>
          <label>
            <span>Link to</span>
            <select
              value={draft.linkKind}
              onChange={(e) => setLinkKind(e.target.value as LinkKind)}
            >
              <option value="none">None</option>
              <option value="blog">Blog post</option>
              <option value="gallery">Photo album</option>
            </select>
          </label>
          {draft.linkKind === "blog" && (
            <label className="timeline-milestone-field-wide">
              <span>Blog post</span>
              <SearchableSelect
                options={blogPostOptions}
                value={draft.blogPostId}
                onChange={(blogPostId) => setDraft((d) => ({ ...d, blogPostId }))}
                placeholder="Search posts by title…"
                emptyMessage="No blog posts match your search"
              />
            </label>
          )}
          {draft.linkKind === "gallery" && (
            <label className="timeline-milestone-field-wide">
              <span>Photo album</span>
              <select
                value={draft.galleryAlbumId ?? ""}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    galleryAlbumId: e.target.value ? Number(e.target.value) : null,
                  }))
                }
              >
                <option value="">Choose an album…</option>
                {albums.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.title}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            <span>Sort order</span>
            <input
              type="number"
              min={0}
              max={9999}
              value={draft.sortOrder}
              onChange={(e) =>
                setDraft((d) => ({ ...d, sortOrder: Number(e.target.value) || 0 }))
              }
            />
          </label>
        </div>

        {err && <p className="timeline-milestone-error">{err}</p>}

        <div className="timeline-milestone-actions">
          {draft.id != null && onDeleted && (
            <button
              type="button"
              className="btn btn-ghost timeline-milestone-delete"
              disabled={busy}
              onClick={() => void onDelete()}
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          )}
          <div className="timeline-milestone-actions-right">
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void onSave()}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function milestoneHref(m: MovementMilestone): string | null {
  if (m.blogSlug) return `/blog/${m.blogSlug}`;
  if (m.gallerySlug) return `/gallery/${m.gallerySlug}`;
  return null;
}

export function milestoneLinkLabel(m: MovementMilestone): string | null {
  if (m.blogTitle) return `Read: ${m.blogTitle}`;
  if (m.galleryTitle) return `View album: ${m.galleryTitle}`;
  return null;
}
